import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useEffectOnce } from 'react-use'

import Icon, { type IconName } from 'components/Icon'
import { getAuthenticationFormValidationSchema } from 'components/status-checks/authentication-form-schema-builders'
import {
  type AuthenticationFormValues,
  getAWSOnboardingAuthenticationDefaultValues,
  isSelectedObservabilityToolAuthenticated,
  getAWSDefaultValuesForReset
} from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolAuthenticateAndSaveButton } from 'components/status-checks/common/ObservabilityToolAuthenticateAndSaveButton'
import { ObservabilityToolAuthenticationStatus } from 'components/status-checks/common/ObservabilityToolAuthenticationStatus'
import { ObservabilityToolAWSAuthenticationDetails } from 'components/status-checks/form/authentication/aws/ObservabilityToolAWSAuthenticationDetails'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import {
  type TeamExternalIntegration,
  TeamExternalIntegrationAuthenticationStatus
} from 'types/api/status-checks/status-check'

export const AWSAuthenticationForm: React.FC<{
  actionButtonText?: string
  externalIntegrations: TeamExternalIntegration[]
  hasActionButtonIcon?: boolean
  actionButtonIconName?: IconName
  hasIntroText?: boolean
  selectedAwsAccountId?: string
  handleNext?: (accountId: string) => void
  onSuccess?: () => void
}> = ({
  actionButtonIconName = 'arrowRight',
  actionButtonText = 'Next',
  externalIntegrations,
  hasActionButtonIcon = true,
  hasIntroText = true,
  selectedAwsAccountId,
  handleNext = () => {},
  onSuccess = () => {}
}) => {
  const { authenticationResponse } = useObservabilityToolAuthenticationContext()
  const { isCreatingAuthenticationOnly, isDrawerFlow, isEdit } =
    useObservabilityToolAuthenticationContext()
  const { getAvailableExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()

  const authenticationFormValidationSchema =
    getAuthenticationFormValidationSchema(
      externalIntegrations,
      isCreatingAuthenticationOnly
    )
  const defaultValues = getAWSOnboardingAuthenticationDefaultValues(
    externalIntegrations,
    selectedAwsAccountId
  )

  const form = useForm<AuthenticationFormValues>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(authenticationFormValidationSchema)
  })
  const { formState, setValue, watch, reset } = form
  const { isSubmitting, isValid } = formState

  // Watches selected form values and re-renders when changed
  const observabilityTool = watch('observabilityTool')
  useEffectOnce(() => {
    if (isCreatingAuthenticationOnly && !isEdit) {
      const externalIntegrations = getAvailableExternalIntegrations(false)
      reset(getAWSDefaultValuesForReset(externalIntegrations))
    }
  })

  const hasAWSAuthentications = Boolean(observabilityTool?.domains?.length)
  const isAuthenticated = isSelectedObservabilityToolAuthenticated(
    observabilityTool,
    externalIntegrations
  )

  const handleTestTeamExternalIntegrationError = () => {
    setValue(
      'externalIntegration.lastAuthenticationStatus',
      TeamExternalIntegrationAuthenticationStatus.UnAuthenticated
    )
  }

  const handleTestTeamExternalIntegrationSuccess = async () => {
    onSuccess()
    setValue(
      'externalIntegration.lastAuthenticationStatus',
      TeamExternalIntegrationAuthenticationStatus.Authenticated
    )
  }

  return (
    <FormProvider {...form}>
      <Grid container {...(isDrawerFlow && { sx: { mr: 6 } })}>
        <Grid item lg={9} xs={12}>
          <Stack spacing={4}>
            <Stack spacing={2}>
              {hasIntroText && hasAWSAuthentications && (
                <Typography mb={2}>
                  What AWS Account ID will be used?
                </Typography>
              )}
              {hasIntroText && !hasAWSAuthentications && (
                <Typography>
                  Gremlin can view your Elastic Load Balancers (ELBs) and help
                  you turn them into services.
                </Typography>
              )}
              <ObservabilityToolAWSAuthenticationDetails />
            </Stack>
            <Box>
              {(!isAuthenticated || isSubmitting) && (
                <ObservabilityToolAuthenticateAndSaveButton
                  externalIntegrations={externalIntegrations}
                  isDisabled={!isValid || isSubmitting}
                  onTestError={handleTestTeamExternalIntegrationError}
                  onTestSuccess={handleTestTeamExternalIntegrationSuccess}
                />
              )}
              {isAuthenticated && !isSubmitting && (
                <Button
                  data-testid="observability-tool-authentication-next-button"
                  endIcon={
                    hasActionButtonIcon && <Icon icon={actionButtonIconName} />
                  }
                  variant="contained"
                  onClick={() => {
                    handleNext(observabilityTool.selectedDomain ?? '')
                  }}
                >
                  {actionButtonText}
                </Button>
              )}
            </Box>
            <ObservabilityToolAuthenticationStatus
              authenticationResponse={authenticationResponse}
            />
          </Stack>
        </Grid>
      </Grid>
    </FormProvider>
  )
}
